import React from "react";
import FloatingCard from "../../ui/FloatingCard";
import { useNavigate } from "react-router-dom";
import { eid } from "../../../utils/entityId";

/**
 * Team Stats Tab Component
 * Displays team statistics and top performers
 */
const TeamStatsTab = ({ teamStatsSummary, stats }) => {
  const navigate = useNavigate();

  return (
    <div className="stats-content-gaming">
      {teamStatsSummary && (
        <div
          className="stats-cards-grid-gaming"
          style={{
            marginBottom: "8px",
            gap: "8px",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          }}
        >
          <FloatingCard
            className="player-stats-card-gaming delay-1"
            variant="outline"
            size="medium"
            style={{ padding: "8px" }}
          >
            <div
              className="stats-card-content-gaming"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <span className="stat-label-small-gaming" style={{ fontSize: "0.8rem" }}>
                🏟️ Games
              </span>
              <span className="stat-value-small-gaming" style={{ fontSize: "1.1rem" }}>
                {teamStatsSummary.gamesPlayed ?? teamStatsSummary.totalMatches ?? 0}
              </span>
            </div>
          </FloatingCard>
          <FloatingCard
            className="player-stats-card-gaming delay-2"
            variant="outline"
            size="medium"
            style={{ padding: "8px" }}
          >
            <div
              className="stats-card-content-gaming"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <span className="stat-label-small-gaming" style={{ fontSize: "0.8rem" }}>
                🏆 Wins
              </span>
              <span className="stat-value-small-gaming" style={{ fontSize: "1.1rem" }}>
                {teamStatsSummary.wins ?? 0}
              </span>
            </div>
          </FloatingCard>
          <FloatingCard
            className="player-stats-card-gaming delay-3"
            variant="outline"
            size="medium"
            style={{ padding: "8px" }}
          >
            <div
              className="stats-card-content-gaming"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <span className="stat-label-small-gaming" style={{ fontSize: "0.8rem" }}>
                📈 Win %
              </span>
              <span className="stat-value-small-gaming" style={{ fontSize: "1.1rem" }}>
                {teamStatsSummary.winPercentage ?? 0}%
              </span>
            </div>
          </FloatingCard>
          {(teamStatsSummary.leagues?.length > 0 || teamStatsSummary.tournaments?.length > 0) && (
            <FloatingCard
              className="player-stats-card-gaming delay-4"
              variant="outline"
              size="medium"
              style={{ padding: "8px" }}
            >
              <div className="stats-card-content-gaming">
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                  <span className="stat-label-small-gaming" style={{ fontSize: "0.8rem" }}>
                    🏅 Competitions
                  </span>
                </div>
                {teamStatsSummary.leagues?.length > 0 && (
                  <div className="stat-item-small-gaming" style={{ marginBottom: "6px" }}>
                    <span className="stat-label-small-gaming" style={{ marginRight: "6px" }}>
                      Leagues
                    </span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {teamStatsSummary.leagues.map((league) => (
                        <button
                          key={league.id || league.name}
                          type="button"
                          onClick={() => {
                            if (league.id) {
                              navigate(`/leagues/${eid(league)}`);
                            }
                          }}
                          style={{
                            background: "rgba(125, 211, 252, 0.12)",
                            border: "1px solid rgba(125, 211, 252, 0.35)",
                            color: "#7dd3fc",
                            padding: "2px 6px",
                            borderRadius: "999px",
                            fontSize: "0.75rem",
                            cursor: league.id ? "pointer" : "default",
                          }}
                        >
                          {league.name || league}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {teamStatsSummary.tournaments?.length > 0 && (
                  <div className="stat-item-small-gaming">
                    <span className="stat-label-small-gaming" style={{ marginRight: "6px" }}>
                      Tournaments
                    </span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {teamStatsSummary.tournaments.map((tournament) => (
                        <button
                          key={tournament.id || tournament.name}
                          type="button"
                          onClick={() => {
                            if (tournament.id) {
                              navigate(`/tournaments/${eid(tournament)}`);
                            }
                          }}
                          style={{
                            background: "rgba(125, 211, 252, 0.12)",
                            border: "1px solid rgba(125, 211, 252, 0.35)",
                            color: "#7dd3fc",
                            padding: "2px 6px",
                            borderRadius: "999px",
                            fontSize: "0.75rem",
                            cursor: tournament.id ? "pointer" : "default",
                          }}
                        >
                          {tournament.name || tournament}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </FloatingCard>
          )}
        </div>
      )}

      {stats.length > 0 ? (
        <div className="stats-cards-grid-gaming">
          {stats.map((stat, index) => (
            <FloatingCard
              key={index}
              className={`player-stats-card-gaming delay-${(index % 4) + 4}`}
              variant="outline"
              size="medium"
            >
              <div className="stats-card-header-gaming">
                <h4 className="player-name-gaming">
                  <span className="player-icon-gaming">⚽</span>
                  {stat.player_name ||
                    `${stat.first_name || ""} ${stat.last_name || ""}`.trim() ||
                    "Player"}
                </h4>
              </div>

              <div className="stats-card-content-gaming">
                <div className="stats-row-gaming">
                  <div className="stat-item-small-gaming">
                    <span className="stat-label-small-gaming">Matches</span>
                    <span className="stat-value-small-gaming">{stat.matches_played ?? 0}</span>
                  </div>
                  <div className="stat-item-small-gaming">
                    <span className="stat-label-small-gaming">Total Score</span>
                    <span className="stat-value-small-gaming">
                      {stat.total_score_contribution ?? stat.total_score ?? 0}
                    </span>
                  </div>
                </div>

                <div className="stats-row-gaming">
                  <div className="stat-item-small-gaming">
                    <span className="stat-label-small-gaming">Avg Rating</span>
                    <span className="stat-value-small-gaming">
                      {stat.average_rating ?? stat.average_score ?? 0}
                    </span>
                  </div>
                  <div className="stat-item-small-gaming highlight">
                    <span className="stat-label-small-gaming">Assists</span>
                    <span className="stat-value-small-gaming">{stat.total_assists ?? 0}</span>
                  </div>
                </div>
              </div>
            </FloatingCard>
          ))}
        </div>
      ) : !teamStatsSummary ? (
        <FloatingCard className="empty-stats-gaming delay-4">
          <div className="empty-content-gaming">
            <div className="empty-icon-gaming">📊</div>
            <h3 className="empty-title-gaming">No Statistics Available</h3>
            <p className="empty-text-gaming">
              No match statistics have been recorded for this team yet. Start playing matches to see
              performance data!
            </p>
          </div>
        </FloatingCard>
      ) : null}
    </div>
  );
};

export default TeamStatsTab;
